// Pure presentation helpers for /admin/analytics. No React, no fetching.
//
// Dates are local shop days (`YYYY-MM-DD`); the API interprets them in the
// shop timezone, so "today" is computed in that zone too.

export const SHOP_TIMEZONE = "Europe/Belgrade";
export const MAX_RANGE_DAYS = 732;
export const DATE_PRESETS = ["7d", "30d", "90d", "thisMonth", "lastMonth", "thisQuarter", "ytd", "12m"];
export const DEFAULT_PRESET = "30d";
export const GRANULARITIES = ["day", "week", "month"];

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86400000;

const toUtcMs = (key) => {
  const [year, month, day] = key.split("-").map(Number);
  return Date.UTC(year, month - 1, day);
};
const fromUtcMs = (ms) => new Date(ms).toISOString().slice(0, 10);

export const isDateKey = (value) => {
  if (typeof value !== "string" || !DATE_KEY.test(value)) return false;
  return fromUtcMs(toUtcMs(value)) === value;
};

export const addDays = (key, days) => fromUtcMs(toUtcMs(key) + days * DAY_MS);

export const daysInRange = (from, to) => Math.round((toUtcMs(to) - toUtcMs(from)) / DAY_MS) + 1;

export const todayKey = (now = new Date(), timeZone = SHOP_TIMEZONE) =>
  new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);

const monthStart = (key) => `${key.slice(0, 7)}-01`;
const monthEnd = (key) => {
  const [year, month] = key.split("-").map(Number);
  return fromUtcMs(Date.UTC(year, month, 0));
};

/** Inclusive local range for a named preset, ending today unless the preset is closed. */
export const presetRange = (preset, today) => {
  switch (preset) {
    case "7d":
      return { dateFrom: addDays(today, -6), dateTo: today };
    case "90d":
      return { dateFrom: addDays(today, -89), dateTo: today };
    case "thisMonth":
      return { dateFrom: monthStart(today), dateTo: today };
    case "lastMonth": {
      const lastDay = addDays(monthStart(today), -1);
      return { dateFrom: monthStart(lastDay), dateTo: lastDay };
    }
    case "thisQuarter": {
      const [year, month] = today.split("-").map(Number);
      const quarterMonth = Math.floor((month - 1) / 3) * 3 + 1;
      return { dateFrom: `${year}-${String(quarterMonth).padStart(2, "0")}-01`, dateTo: today };
    }
    case "ytd":
      return { dateFrom: `${today.slice(0, 4)}-01-01`, dateTo: today };
    case "12m":
      return { dateFrom: addDays(today, -364), dateTo: today };
    case "30d":
    default:
      return { dateFrom: addDays(today, -29), dateTo: today };
  }
};

/** A whole calendar month (`YYYY-MM`) as a range, for the monthly report. */
export const monthRange = (month) => {
  const start = `${month}-01`;
  return { dateFrom: start, dateTo: monthEnd(start) };
};

export const defaultGranularity = (days) => (days <= 62 ? "day" : days <= 190 ? "week" : "month");

export const validateRange = ({ dateFrom, dateTo }) => {
  if (!isDateKey(dateFrom) || !isDateKey(dateTo)) return "invalid";
  if (dateFrom > dateTo) return "order";
  if (daysInRange(dateFrom, dateTo) > MAX_RANGE_DAYS) return "tooLong";
  return null;
};

/**
 * Reads the shared filter state from the URL so every section, the browser
 * history and shared links agree. Unknown or invalid values fall back to the
 * default preset instead of reaching the API.
 */
export const readRangeState = (searchParams, today) => {
  const preset = searchParams.get("preset");
  const from = searchParams.get("from");
  const to = searchParams.get("to");
  const granularity = searchParams.get("g");

  let range;
  let activePreset;
  if (from && to && validateRange({ dateFrom: from, dateTo: to }) === null) {
    range = { dateFrom: from, dateTo: to };
    activePreset = DATE_PRESETS.includes(preset) ? preset : "custom";
  } else {
    activePreset = DATE_PRESETS.includes(preset) ? preset : DEFAULT_PRESET;
    range = presetRange(activePreset, today);
  }
  const days = daysInRange(range.dateFrom, range.dateTo);
  return {
    ...range,
    preset: activePreset,
    days,
    granularity: GRANULARITIES.includes(granularity) ? granularity : defaultGranularity(days),
    granularityExplicit: GRANULARITIES.includes(granularity),
  };
};

export const writeRangeState = (searchParams, next) => {
  const params = new URLSearchParams(searchParams);
  if (next.preset && next.preset !== "custom") {
    params.set("preset", next.preset);
    params.delete("from");
    params.delete("to");
  } else {
    params.set("preset", "custom");
    params.set("from", next.dateFrom);
    params.set("to", next.dateTo);
  }
  if (next.granularity) params.set("g", next.granularity);
  else params.delete("g");
  return params;
};

// ── Formatting ───────────────────────────────────────────────────────────────

const locale = (lang) => (lang === "en" ? "en-GB" : "sq-AL");

export const isMissing = (value) => value === null || value === undefined || Number.isNaN(value);

export const formatMoney = (value, lang, { compact = false } = {}) => {
  if (isMissing(value)) return "—";
  return new Intl.NumberFormat(locale(lang), {
    style: "currency",
    currency: "EUR",
    notation: compact ? "compact" : "standard",
    minimumFractionDigits: compact ? 0 : 2,
    maximumFractionDigits: compact ? 1 : 2,
  }).format(value);
};

export const formatNumber = (value, lang, digits = 0) => {
  if (isMissing(value)) return "—";
  return new Intl.NumberFormat(locale(lang), { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(value);
};

/** `ratio` is 0–1 (0.1234 → 12.3%). */
export const formatPercent = (ratio, lang, digits = 1) => {
  if (isMissing(ratio)) return "—";
  return new Intl.NumberFormat(locale(lang), {
    style: "percent",
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  }).format(ratio);
};

/**
 * Change badge model. `invert` marks metrics where up is bad (refunds).
 * A null change (no comparable previous value) is "none", never 0%.
 */
export const describeChange = (change, { invert = false } = {}) => {
  if (isMissing(change)) return { direction: "none", tone: "neutral" };
  if (Math.abs(change) < 0.0005) return { direction: "flat", tone: "neutral" };
  const up = change > 0;
  const good = invert ? !up : up;
  return { direction: up ? "up" : "down", tone: good ? "positive" : "negative" };
};

export const formatChange = (change, lang) => {
  if (isMissing(change)) return "—";
  const text = formatPercent(Math.abs(change), lang, 1);
  return change > 0 ? `+${text}` : change < 0 ? `−${text}` : text;
};

export const formatDate = (key, lang, options = { day: "2-digit", month: "short", year: "numeric" }) => {
  if (!key) return "—";
  const date = key.length === 10 ? new Date(`${key}T12:00:00Z`) : new Date(key);
  return new Intl.DateTimeFormat(locale(lang), { timeZone: key.length === 10 ? "UTC" : SHOP_TIMEZONE, ...options }).format(date);
};

export const formatDateTime = (iso, lang) =>
  iso
    ? new Intl.DateTimeFormat(locale(lang), {
        timeZone: SHOP_TIMEZONE,
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(iso))
    : "—";

export const bucketLabel = (bucket, granularity, lang) => {
  if (!bucket) return "";
  if (granularity === "month") return formatDate(bucket, lang, { month: "short", year: "2-digit" });
  return formatDate(bucket, lang, { day: "2-digit", month: "short" });
};

/** ISO weekday (1 = Monday) to a short local name. */
export const weekdayLabel = (isoDay, lang) =>
  new Intl.DateTimeFormat(locale(lang), { weekday: "short", timeZone: "UTC" }).format(
    new Date(Date.UTC(2026, 0, 4 + isoDay)) // 2026-01-05 is a Monday
  );

export const COST_STATUS_TONE = { complete: "success", partial: "warning", unavailable: "danger", none: "neutral" };

/** Interpolates `{name}` placeholders. */
export const fill = (template, values = {}) =>
  Object.entries(values).reduce((text, [key, value]) => text.split(`{${key}}`).join(String(value)), String(template || ""));

export const COST_ROLES = ["admin", "owner"];
export const canSeeCost = (role) => COST_ROLES.includes(String(role || "").toLowerCase());

export const KNOWN_FAMILIES = ["shoes", "textile", "accessories"];
export const familyLabel = (tt, family) => (KNOWN_FAMILIES.includes(family) ? tt(`family_${family}`) : family || "—");

// ── Insights ─────────────────────────────────────────────────────────────────

const PERCENT_KEYS = new Set(["share", "topShare", "threshold", "change", "discountRate", "previousDiscountRate", "returnRate", "productsFor80Share"]);
const MONEY_KEYS = new Set(["retailValue", "netRevenue", "previousNetRevenue", "current", "previous", "discounts", "grossSales", "averageOrderValue"]);
const HIDDEN_EVIDENCE_KEYS = new Set(["productId", "categoryKey"]);

export const formatInsightValue = (key, value, lang) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (PERCENT_KEYS.has(key)) return key === "change" && value > 0 ? `+${formatPercent(value, lang)}` : formatPercent(value, lang);
  if (MONEY_KEYS.has(key)) return formatMoney(value, lang);
  return formatNumber(value, lang, 1);
};

export const insightTemplateKey = (insight) =>
  insight.type === "category_trend" ? `insight_category_trend_${insight.params.direction}` : `insight_${insight.type}`;

export const insightRuleKey = (insight) => `insight_${insight.type}_rule`;

/** The finding as a sentence, with every number formatted for display. */
export const describeInsight = (insight, tt, lang) => {
  const values = Object.fromEntries(Object.entries(insight.params || {}).map(([key, value]) => [key, formatInsightValue(key, value, lang)]));
  return {
    text: fill(tt(insightTemplateKey(insight)), values),
    rule: fill(tt(insightRuleKey(insight)), values),
  };
};

export const evidenceColumns = (rows) => {
  const keys = [];
  for (const row of rows || []) {
    for (const key of Object.keys(row)) {
      if (!HIDDEN_EVIDENCE_KEYS.has(key) && !keys.includes(key)) keys.push(key);
    }
  }
  return keys;
};

/** KPI badge text for a cost block: "recorded cost", "partly estimated" or "estimated". */
export const costBasisLabel = (tt, cost) => (cost?.basis ? tt(`basis_${cost.basis}`) : null);
